import { GetBaladyToken } from "./index";

// Cookie names for token storage
const BALADY_TOKEN_COOKIE = "balady_access_token";
const BALADY_TOKEN_EXPIRY_COOKIE = "balady_token_expiry";

// Simple cookie utilities
const setCookie = (name: string, value: string, days: number = 1): void => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
};

const getCookie = (name: string): string | null => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

const deleteCookie = (name: string): void => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

// Check if token is expired (with 5 min buffer)
const isTokenExpired = (): boolean => {
  // console.log("isTokenExpired", getCookie(BALADY_TOKEN_EXPIRY_COOKIE));
  const expiryStr = getCookie(BALADY_TOKEN_EXPIRY_COOKIE);
  // console.log("expiryStr", expiryStr);
  if (!expiryStr) return true;
  
  try {
    const expiry = new Date(expiryStr);
    const now = new Date();
    return now.getTime() > (expiry.getTime() - (5 * 60 * 1000));
  } catch {
    return true;
  }
};

// Main function: get token from cookies if not expired, otherwise get new one
export const getBaladyAccessToken = async (): Promise<string> => {
  try {
    // Check if we have a valid cached token
    const cachedToken = getCookie(BALADY_TOKEN_COOKIE);
    if (cachedToken && !isTokenExpired()) {
      // console.log("Using cached Balady token");
      return cachedToken;
    }

    // console.log("Getting new Balady token from backend...");
    
    // Get new token from backend
    const response = await GetBaladyToken();
    
    if (response.data && response.data.accessToken) {
      const accessToken = response.data.accessToken;
      const expiresIn = parseInt(response.data.expiresUtc) ;
      // console.log("response.data.expiresUtc", response.data.expiresUtc);
      // console.log("expiresIn", expiresIn);
      // Save token and expiry to cookies
      const expiryDate = new Date(Date.now() + (expiresIn * 1000));
      setCookie(BALADY_TOKEN_COOKIE, accessToken, 1);
      setCookie(BALADY_TOKEN_EXPIRY_COOKIE, expiryDate.toISOString(), 1);
      
      // console.log("New Balady token saved to cookies");
      return accessToken;
    } else {
      throw new Error("Invalid response from backend");
    }
  } catch (error) {
    console.error("Error getting Balady token:", error);
    throw error;
  }
};


// Clear token from cookies
export const clearBaladyToken = (): void => {
  deleteCookie(BALADY_TOKEN_COOKIE);
  deleteCookie(BALADY_TOKEN_EXPIRY_COOKIE);
  // console.log("Balady token cleared");
};

// Check if token exists and is valid
export const hasValidBaladyToken = (): boolean => {
  const token = getCookie(BALADY_TOKEN_COOKIE);
  return token !== null && !isTokenExpired();
};

// Helper function to get point data with automatic token refresh on 401
export const getPointDataWithTokenRefresh = async (
  x: string,
  y: string,
  requestNumber?: string
): Promise<any> => {
  try {
    const token = await getBaladyAccessToken();
    const { GetPointData } = await import("./index");
    return await GetPointData(token, x, y, requestNumber);
  } catch (error: any) {
    // If we get a 401, the token was already cleared by GetPointData
    // Try to get a fresh token and retry once
    if (error.response?.status === 401) {
      console.log("Retrying with fresh token...");
      try {
        const freshToken = await getBaladyAccessToken();
        const { GetPointData } = await import("./index");
        return await GetPointData(freshToken, x, y, requestNumber);
      } catch (retryError) {
        console.error("Failed to get point data even with fresh token:", retryError);
        throw retryError;
      }
    }
    throw error;
  }
};
